'use client';

import React, { type FC, useState } from 'react';

import Grid from '../Grid/Grid';
import { Call } from '../Icons/Paths/Call';
import { Close } from '../Icons/Paths/Close';
import { Dealer } from '../Icons/Paths/Dealer';
import { Hamburger } from '../Icons/Paths/Hamburger';
import { HeartDefault } from '../Icons/Paths/HeartDefault';
import { HeartHover } from '../Icons/Paths/HeartHover';
import { Search } from '../Icons/Paths/Search';
import Logo from '../Logo/Logo';
import { Typography } from '../Typography/Typography';
import LanguageList from './Components/LanguageList';
import NavigationBurgerMenu from './Components/NavigationBurgerMenu';
import NavigationDropdown from './Components/NavigationDropdown';
import { DEFAULT_LOCALE, MAX_FAVES_DISPLAY_NO } from './Constants/Header.constants';
import type { HeaderLinkProps, HeaderProps } from './Header.types';
import { useLangList } from './Hooks/UseLangList';
import { useNavigationItem } from './Hooks/UseNavigationItem';
import { getCurrentLang, hasHeaderItems } from './Utils/HeaderItemHelpers';

import styles from './Styles/Header.module.css';

// eslint-disable-next-line jsx-a11y/anchor-has-content
const DefaultLinkComponent = (props: HeaderLinkProps) => <a {...props} />;

export const Header: FC<HeaderProps> = React.forwardRef<HTMLElement, HeaderProps>(
  (
    {
      accountItemConfig,
      auxiliaryDetails,
      callItemConfig,
      currentRoute,
      dataTestId,
      favoritesItemConfig,
      langItemConfig,
      LinkComponent,
      locale = DEFAULT_LOCALE,
      logoHref,
      navigation,
      onToggleBurgerMenu,
      trackingFn,
      LogoElement = Logo,
      SearchBar,
      toggleSearchBar,
    },
    ref,
  ) => {
    // State items
    const [isNavTrayOpen, setIsNavTrayOpen] = useState(false);
    const [activeNavItem, setActiveNavItem] = useState<string | undefined>(undefined);
    const [isFavHovered, setIsFavHovered] = useState(false);

    const toggleBurgerMenu = (isOpen: boolean) => {
      setIsNavTrayOpen(isOpen);
      if (typeof onToggleBurgerMenu === 'function') onToggleBurgerMenu(isOpen);
    };

    const resetMenuState = () => {
      setActiveNavItem(undefined);
      toggleBurgerMenu(false);
    };

    const { itemOnClick } = useNavigationItem(activeNavItem, setActiveNavItem, resetMenuState);

    // Link component
    const Link = (LinkComponent || DefaultLinkComponent) as (props: HeaderLinkProps) => JSX.Element;

    // Header items
    const { hasFaves, hasLang, hasAccount, hasCall } = hasHeaderItems(
      favoritesItemConfig,
      langItemConfig,
      accountItemConfig,
      callItemConfig,
    );
    const favesCount = favoritesItemConfig?.favoritesNumber || 0;

    // Lang
    const currentLang = hasLang
      ? getCurrentLang(langItemConfig!.currentLang, langItemConfig!.options)
      : undefined;
    const LangIco = currentLang?.icon;
    const { isLangListOpen, keyboardOpen, setIsFocused, setIsHovering } = useLangList();

    return (
      <header
        className={`${styles.header} ${isNavTrayOpen ? styles.isBurgerOpen : ''}`}
        data-test-id={dataTestId}
        data-site-header
        ref={ref}
      >
        <Grid.Container className={styles.headerInner}>
          <Grid.Row gutter={{ mobile: 8, tablet: 10, desktop: 24 }}>
            <Grid.Col className={styles.colLeft}>
              <Link
                aria-label={locale.logoLabel}
                className={styles.logo}
                href={logoHref}
                onClick={() =>
                  itemOnClick({
                    fn: trackingFn,
                    obj: { label: 'Logo', href: logoHref },
                  })
                }
              >
                <LogoElement />
              </Link>
              {SearchBar && <SearchBar />}
            </Grid.Col>
            <Grid.Col className={styles.colRight}>
              {/*
               *** Mobile Search Button
               */}
              {SearchBar && (
                <button
                  aria-label={'Search'}
                  className={`${styles.notHorizontalNav} ${styles.item}`}
                  onClick={() =>
                    itemOnClick(
                      {
                        fn: trackingFn,
                        obj: { label: 'Mobile Search Toggle', action: 'open' },
                      },
                      () => toggleSearchBar?.(true),
                    )
                  }
                >
                  <Search />
                </button>
              )}
              {/*
               *** Favorite Button
               */}
              {hasFaves && (
                <a
                  aria-label={favoritesItemConfig!.label}
                  className={`${styles.item} ${styles.faves}`}
                  href={favoritesItemConfig!.href}
                  rel="nofollow"
                  onMouseOver={() => setIsFavHovered(true)}
                  onMouseOut={() => setIsFavHovered(false)}
                  onClick={() =>
                    itemOnClick(
                      {
                        fn: trackingFn,
                        obj: {
                          label: favoritesItemConfig!.label,
                          href: favoritesItemConfig!.href,
                        },
                      },
                      favoritesItemConfig!.onClick,
                    )
                  }
                >
                  {isFavHovered ? <HeartHover /> : <HeartDefault />}
                  <Typography
                    variant="caption4"
                    Component="span"
                    aria-label={locale.favoritesCountLabel}
                    className={`${styles.counter} ${favesCount > 0 ? styles.active : ''}`}
                  >
                    {favesCount > MAX_FAVES_DISPLAY_NO ? MAX_FAVES_DISPLAY_NO : favesCount}
                  </Typography>
                  <Typography variant="subheading3">{favoritesItemConfig!.label}</Typography>
                </a>
              )}
              {/*
               *** Account Button
               */}
              {hasAccount && (
                <button
                  aria-label={accountItemConfig!.label}
                  {...(accountItemConfig?.dataTestId && {
                    'data-test-id': accountItemConfig.dataTestId,
                  })}
                  className={`${styles.horizontalNavOnly} ${styles.item}`}
                  onClick={() =>
                    itemOnClick(
                      {
                        fn: trackingFn,
                        obj: { label: accountItemConfig!.label },
                      },
                      accountItemConfig!.onClick,
                    )
                  }
                >
                  <Dealer />
                  <Typography variant="subheading3">{accountItemConfig!.label}</Typography>
                </button>
              )}
              {/*
               *** Lang Select
               */}

              {hasLang && currentLang && (
                <div className={styles.langWrapper}>
                  <button
                    aria-haspopup="menu"
                    aria-label={`${locale.langListHeading} - ${locale.spaceBarNotification}`}
                    className={`${styles.horizontalNavOnly} ${styles.item} ${
                      isLangListOpen ? styles.focused : ''
                    }`}
                    onBlur={() => setIsFocused(false)}
                    onFocus={() => setIsFocused(true)}
                    onKeyDown={e => keyboardOpen(e)}
                    onMouseOut={() => setIsHovering(false)}
                    onMouseOver={() => setIsHovering(true)}
                  >
                    {LangIco}
                    <Typography variant="subheading3">{currentLang.shortName}</Typography>
                  </button>
                  {isLangListOpen && (
                    <LanguageList
                      dataTestId="header-language-list"
                      heading={locale.langListHeading}
                      itemOnClick={itemOnClick}
                      onFocusEvents={setIsFocused}
                      onHoverEvents={setIsHovering}
                      options={langItemConfig!.options}
                      trackingFn={trackingFn}
                    />
                  )}
                </div>
              )}

              {/*
               *** Call Button
               */}
              {hasCall && (
                <Link
                  aria-label={callItemConfig!.label}
                  className={`${styles.horizontalNavOnly} ${styles.item} ${styles.asideItem}`}
                  href={callItemConfig!.href}
                  onClick={() =>
                    itemOnClick(
                      {
                        fn: trackingFn,
                        obj: {
                          label: callItemConfig!.label,
                          href: callItemConfig!.href,
                        },
                      },
                      callItemConfig!.onClick,
                    )
                  }
                >
                  <Call />
                  <Typography variant="subheading3">{callItemConfig!.label}</Typography>
                </Link>
              )}
              {/*
               *** Mobile menu Toggle
               */}
              <button
                aria-label={DEFAULT_LOCALE.burgerMenuButtonLabel}
                className={`${styles.notHorizontalNav} ${styles.item}`}
                onClick={() =>
                  itemOnClick(
                    {
                      fn: trackingFn,
                      obj: {
                        label: 'Burger Menu Toggle',
                        action: isNavTrayOpen ? 'close' : 'open',
                      },
                    },
                    () => toggleBurgerMenu(!isNavTrayOpen),
                    false,
                  )
                }
              >
                {isNavTrayOpen ? <Close /> : <Hamburger />}
              </button>
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
        {/*
         *** Navigation
         */}
        <div className={`${styles.navWrapper} ${isNavTrayOpen ? 'navOpen' : ''}`}>
          <NavigationBurgerMenu
            Link={Link}
            accountItemConfig={accountItemConfig}
            activeNavItem={activeNavItem}
            auxiliaryDetails={auxiliaryDetails}
            currentLang={currentLang?.langCode}
            dataTestId={`${dataTestId}-navigation`}
            itemOnClick={itemOnClick}
            langItemConfig={langItemConfig}
            locale={locale}
            navigation={navigation}
            setActiveNavItem={setActiveNavItem}
            trackingFn={trackingFn}
          />
          <NavigationDropdown
            Link={Link}
            activeNavItem={activeNavItem}
            currentRoute={currentRoute}
            dataTestId={`${dataTestId}-navigation`}
            itemOnClick={itemOnClick}
            locale={locale}
            navigation={navigation}
            setActiveNavItem={setActiveNavItem}
            trackingFn={trackingFn}
          />
        </div>
      </header>
    );
  },
);

Header.displayName = 'Header';
